import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Save, RotateCcw, Star, Eye, EyeOff, Trash2, Plus } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/lib/constants";
import ImageUploader from "@/components/ImageUploader";

const TABS = [
  { id: "services", label: "Prestations & tarifs" },
  { id: "texts", label: "Textes" },
  { id: "photos", label: "Photos" },
  { id: "assistant", label: "Assistant IA" },
  { id: "offer", label: "Offre mise en avant" },
  { id: "contact", label: "Coordonnées" },
];

const LANG_LABEL = { fr: "Français", he: "עברית", en: "English" };

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState("services");
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/chat/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (user === null || !content) {
    return <div className="min-h-screen bg-ivory flex items-center justify-center text-taupe">Chargement…</div>;
  }
  if (user === false) return null;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        categories: content.categories,
        services: content.services,
        offer: content.offer,
        contact: content.contact,
        texts: content.texts || {},
        images: content.images || {},
        assistant: content.assistant || { enabled: true },
      };
      const { data } = await api.put("/content", payload);
      setContent(data);
      toast.success("Modifications enregistrées");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Réinitialiser tout le contenu aux valeurs par défaut ?")) return;
    try {
      const { data } = await api.post("/content/reset");
      setContent(data);
      toast.success("Contenu réinitialisé");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const updateService = (id, patch) =>
    setContent((c) => ({ ...c, services: c.services.map((s) => (s.id === id ? { ...s, ...patch } : s)) }));

  const addService = (categoryId) =>
    setContent((c) => ({
      ...c,
      services: [
        ...c.services,
        {
          id: `svc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          category: categoryId || c.categories[0]?.id,
          fr: { name: "Nouvelle prestation" },
          he: { name: "טיפול חדש" },
          en: { name: "New service" },
          price: 0,
          quote: false,
          active: true,
          featured: false,
        },
      ],
    }));

  const removeService = (id) => {
    if (!window.confirm("Supprimer définitivement cette prestation ?")) return;
    setContent((c) => ({ ...c, services: c.services.filter((s) => s.id !== id) }));
  };
  const updateServiceName = (id, lang, name) =>
    setContent((c) => ({ ...c, services: c.services.map((s) => (s.id === id ? { ...s, [lang]: { ...s[lang], name } } : s)) }));

  const updateText = (lang, section, field, val) =>
    setContent((c) => ({
      ...c,
      texts: {
        ...(c.texts || {}),
        [lang]: {
          ...((c.texts || {})[lang] || {}),
          [section]: { ...(((c.texts || {})[lang] || {})[section] || {}), [field]: val },
        },
      },
    }));
  const textVal = (lang, section, field) => (((content.texts || {})[lang] || {})[section] || {})[field] || "";

  const setImage = (key, val) => setContent((c) => ({ ...c, images: { ...(c.images || {}), [key]: val } }));
  const setGalleryImage = (idx, val) =>
    setContent((c) => {
      const g = [...((c.images || {}).gallery || [])];
      g[idx] = val;
      return { ...c, images: { ...(c.images || {}), gallery: g } };
    });
  const gallery = (content.images || {}).gallery || [];

  const catLabel = (cid) => content.categories.find((c) => c.id === cid)?.fr || cid;

  return (
    <div dir="ltr" className="min-h-screen bg-cream">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-sand">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="" className="h-10 w-10 rounded-full object-cover" />
            <div>
              <p className="font-serif text-lg text-ink leading-none">Administration</p>
              <p className="text-xs text-taupe">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={resetDefaults} className="flex items-center gap-2 rounded-full border border-sand px-4 py-2 text-sm text-taupe hover:border-gold hover:text-gold transition-colors" data-testid="admin-reset">
              <RotateCcw className="h-4 w-4" /> Réinitialiser
            </button>
            <button onClick={save} disabled={saving} className="flex items-center gap-2 rounded-full bg-gold px-5 py-2 text-sm font-medium text-white hover:brightness-105 transition-[filter] disabled:opacity-60" data-testid="admin-save">
              <Save className="h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
            <button onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-2 rounded-full border border-sand px-4 py-2 text-sm text-taupe hover:text-red-600 transition-colors" data-testid="admin-logout">
              <LogOut className="h-4 w-4" /> Déconnexion
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-5 flex gap-1 -mb-px">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm border-b-2 transition-colors ${tab === t.id ? "border-gold text-gold" : "border-transparent text-taupe hover:text-ink"}`}
              data-testid={`admin-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        {tab === "services" && (
          <div className="space-y-3" data-testid="admin-services">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
              <p className="text-sm text-taupe">Modifiez, ajoutez ou supprimez des prestations. Changez la catégorie, le nom (FR/HE/EN), le prix, activez/désactivez ou mettez en avant.</p>
              <button onClick={() => addService()} className="flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm font-medium text-white hover:bg-taupe transition-colors" data-testid="admin-add-service">
                <Plus className="h-4 w-4" /> Ajouter une prestation
              </button>
            </div>
            {content.services.map((s) => (
              <div key={s.id} className={`bg-white rounded-xl border p-4 ${s.active === false ? "border-sand opacity-60" : "border-sand"}`} data-testid={`admin-service-${s.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <select value={s.category} onChange={(e) => updateService(s.id, { category: e.target.value })}
                    className="rounded-lg border border-sand bg-ivory px-3 py-1.5 text-xs uppercase tracking-wide text-gold font-medium outline-none focus:border-gold" data-testid={`category-select-${s.id}`}>
                    {content.categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.fr}</option>
                    ))}
                  </select>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateService(s.id, { featured: !s.featured })} title="Mettre en avant"
                      className={`p-1.5 rounded-full ${s.featured ? "text-gold" : "text-taupe/40 hover:text-gold"}`} data-testid={`feature-${s.id}`}>
                      <Star className="h-4 w-4" fill={s.featured ? "#C89B5A" : "none"} />
                    </button>
                    <button onClick={() => updateService(s.id, { active: s.active === false })} title="Activer/désactiver"
                      className="p-1.5 rounded-full text-taupe hover:text-ink" data-testid={`toggle-${s.id}`}>
                      {s.active === false ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button onClick={() => removeService(s.id)} title="Supprimer"
                      className="p-1.5 rounded-full text-taupe/50 hover:text-red-600 transition-colors" data-testid={`delete-${s.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="grid md:grid-cols-3 gap-3">
                  {["fr", "he", "en"].map((lg) => (
                    <div key={lg}>
                      <label className="block text-[11px] uppercase text-taupe/70 mb-1">{lg}</label>
                      <input value={s[lg]?.name || ""} onChange={(e) => updateServiceName(s.id, lg, e.target.value)} dir={lg === "he" ? "rtl" : "ltr"}
                        className="w-full rounded-lg border border-sand bg-ivory px-3 py-2 text-sm text-ink outline-none focus:border-gold" data-testid={`name-${s.id}-${lg}`} />
                    </div>
                  ))}
                </div>
                <div className="mt-3 flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-taupe">
                    Prix (₪)
                    <input type="number" value={s.price ?? ""} disabled={s.quote} onChange={(e) => updateService(s.id, { price: e.target.value === "" ? null : Number(e.target.value) })}
                      className="w-28 rounded-lg border border-sand bg-ivory px-3 py-2 text-sm text-ink outline-none focus:border-gold disabled:opacity-50" data-testid={`price-${s.id}`} />
                  </label>
                  <label className="flex items-center gap-2 text-sm text-taupe">
                    <input type="checkbox" checked={!!s.quote} onChange={(e) => updateService(s.id, { quote: e.target.checked })} data-testid={`quote-${s.id}`} />
                    Sur devis
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "texts" && (
          <div className="space-y-6 max-w-4xl" data-testid="admin-texts">
            <p className="text-sm text-taupe">Modifiez les textes du site dans les trois langues. Les champs vides reprennent le texte par défaut.</p>
            {["fr", "he", "en"].map((lg) => (
              <div key={lg} className="bg-white rounded-xl border border-sand p-5">
                <h3 className="font-serif text-lg text-gold mb-4">{LANG_LABEL[lg]}</h3>
                <div className="space-y-3" dir={lg === "he" ? "rtl" : "ltr"}>
                  <TextField label="Accueil — sur-titre" value={textVal(lg, "hero", "overline")} onChange={(v) => updateText(lg, "hero", "overline", v)} testid={`text-hero-overline-${lg}`} />
                  <div className="grid md:grid-cols-2 gap-3">
                    <TextField label="Accueil — titre (ligne 1)" value={textVal(lg, "hero", "title1")} onChange={(v) => updateText(lg, "hero", "title1", v)} testid={`text-hero-title1-${lg}`} />
                    <TextField label="Accueil — titre (ligne 2)" value={textVal(lg, "hero", "title2")} onChange={(v) => updateText(lg, "hero", "title2", v)} testid={`text-hero-title2-${lg}`} />
                  </div>
                  <TextArea label="Accueil — sous-titre" value={textVal(lg, "hero", "subtitle")} onChange={(v) => updateText(lg, "hero", "subtitle", v)} testid={`text-hero-subtitle-${lg}`} />
                  <TextField label="Accueil — mention" value={textVal(lg, "hero", "mention")} onChange={(v) => updateText(lg, "hero", "mention", v)} testid={`text-hero-mention-${lg}`} />
                  <TextField label="À propos — titre" value={textVal(lg, "about", "title")} onChange={(v) => updateText(lg, "about", "title", v)} testid={`text-about-title-${lg}`} />
                  <TextArea label="À propos — texte" value={textVal(lg, "about", "body")} onChange={(v) => updateText(lg, "about", "body", v)} testid={`text-about-body-${lg}`} />
                  <TextField label="L'institut — titre" value={textVal(lg, "institute", "title")} onChange={(v) => updateText(lg, "institute", "title", v)} testid={`text-institute-title-${lg}`} />
                  <TextArea label="L'institut — sous-titre" value={textVal(lg, "institute", "subtitle")} onChange={(v) => updateText(lg, "institute", "subtitle", v)} testid={`text-institute-subtitle-${lg}`} />
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "photos" && (
          <div className="space-y-8 max-w-4xl" data-testid="admin-photos">
            <p className="text-sm text-taupe">Remplacez chaque photo : téléversez une image (JPG, PNG, WebP, max 8 Mo) ou collez une URL. Pensez à cliquer sur « Enregistrer ».</p>
            <div>
              <h3 className="font-serif text-lg text-ink mb-3">Photo principale (accueil)</h3>
              <div className="max-w-xs"><ImageUploader value={(content.images || {}).hero} onChange={(v) => setImage("hero", v)} testid="img-hero" aspect="aspect-[4/5]" /></div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-serif text-lg text-ink mb-3">À propos — grande photo</h3>
                <ImageUploader value={(content.images || {}).aboutMain} onChange={(v) => setImage("aboutMain", v)} testid="img-about-main" aspect="aspect-[3/4]" />
              </div>
              <div>
                <h3 className="font-serif text-lg text-ink mb-3">À propos — petite photo</h3>
                <ImageUploader value={(content.images || {}).aboutSmall} onChange={(v) => setImage("aboutSmall", v)} testid="img-about-small" aspect="aspect-square" />
              </div>
            </div>
            <div>
              <h3 className="font-serif text-lg text-ink mb-3">Galerie « L'institut » (6 photos)</h3>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {Array.from({ length: 6 }).map((_, idx) => (
                  <ImageUploader key={idx} value={gallery[idx]} onChange={(v) => setGalleryImage(idx, v)} testid={`img-gallery-${idx}`} aspect="aspect-[4/5]" />
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "assistant" && (
          <div className="max-w-2xl space-y-6" data-testid="admin-assistant">
            <div className="bg-white rounded-xl border border-sand p-6">
              <label className="flex items-center gap-3 text-sm text-ink">
                <input type="checkbox" checked={(content.assistant || {}).enabled !== false}
                  onChange={(e) => setContent((c) => ({ ...c, assistant: { ...(c.assistant || {}), enabled: e.target.checked } }))}
                  data-testid="assistant-enabled" />
                Activer l'assistant « Votre conseillère beauté » sur le site
              </label>
              <p className="mt-2 text-xs text-taupe">L'assistant utilise automatiquement vos prestations, tarifs et coordonnées ci-dessus (source unique). Modèle : GPT-4o-mini via la clé universelle Emergent.</p>
            </div>

            <div className="bg-white rounded-xl border border-sand p-6">
              <h3 className="font-serif text-lg text-ink mb-4">Statistiques (anonymes)</h3>
              {stats ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="assistant-stats">
                  <Stat label="Conversations" value={stats.conversations || 0} />
                  <Stat label="Messages" value={stats.messages || 0} />
                  <Stat label="Clics WhatsApp" value={stats.whatsapp_clicks || 0} />
                  <Stat label="FR" value={stats.lang_fr || 0} />
                  <Stat label="עברית" value={stats.lang_he || 0} />
                  <Stat label="EN" value={stats.lang_en || 0} />
                  <Stat label="Avis positifs" value={stats.feedback_yes || 0} />
                  <Stat label="Avis négatifs" value={stats.feedback_no || 0} />
                  <Stat label="Erreurs IA" value={stats.errors || 0} />
                </div>
              ) : (
                <p className="text-sm text-taupe">Aucune donnée pour le moment.</p>
              )}
            </div>
          </div>
        )}

        {tab === "offer" && (
          <div className="bg-white rounded-xl border border-sand p-6 max-w-2xl" data-testid="admin-offer">
            <label className="flex items-center gap-2 text-sm text-taupe mb-5">
              <input type="checkbox" checked={content.offer.active !== false} onChange={(e) => setContent((c) => ({ ...c, offer: { ...c.offer, active: e.target.checked } }))} data-testid="offer-active" />
              Afficher l'offre sur le site
            </label>
            {["fr", "he", "en"].map((lg) => (
              <div key={lg} className="mb-4">
                <label className="block text-[11px] uppercase text-taupe/70 mb-1">Titre {lg}</label>
                <input value={content.offer[lg]?.title || ""} dir={lg === "he" ? "rtl" : "ltr"}
                  onChange={(e) => setContent((c) => ({ ...c, offer: { ...c.offer, [lg]: { ...c.offer[lg], title: e.target.value } } }))}
                  className="w-full rounded-lg border border-sand bg-ivory px-3 py-2 text-sm text-ink outline-none focus:border-gold" data-testid={`offer-title-${lg}`} />
              </div>
            ))}
            <label className="block text-sm text-taupe">Prix (₪)
              <input type="number" value={content.offer.price ?? ""} onChange={(e) => setContent((c) => ({ ...c, offer: { ...c.offer, price: Number(e.target.value) } }))}
                className="ml-2 w-28 rounded-lg border border-sand bg-ivory px-3 py-2 text-sm text-ink outline-none focus:border-gold" data-testid="offer-price" />
            </label>
          </div>
        )}

        {tab === "contact" && (
          <div className="bg-white rounded-xl border border-sand p-6 max-w-2xl space-y-4" data-testid="admin-contact">
            <Field label="Téléphone (affiché)" value={content.contact.phone_display} onChange={(v) => setContent((c) => ({ ...c, contact: { ...c.contact, phone_display: v } }))} testid="contact-phone-display" />
            <Field label="Téléphone (international, ex: 972555004933)" value={content.contact.phone_intl} onChange={(v) => setContent((c) => ({ ...c, contact: { ...c.contact, phone_intl: v } }))} testid="contact-phone-intl" />
            <Field label="Email" value={content.contact.email} onChange={(v) => setContent((c) => ({ ...c, contact: { ...c.contact, email: v } }))} testid="contact-email" />
            <Field label="Instagram (@)" value={content.contact.instagram_handle} onChange={(v) => setContent((c) => ({ ...c, contact: { ...c.contact, instagram_handle: v } }))} testid="contact-ig-handle" />
            <Field label="Lien Instagram" value={content.contact.instagram_url} onChange={(v) => setContent((c) => ({ ...c, contact: { ...c.contact, instagram_url: v } }))} testid="contact-ig-url" />
            <Field label="Lien Google Maps" value={content.contact.maps_url} onChange={(v) => setContent((c) => ({ ...c, contact: { ...c.contact, maps_url: v } }))} testid="contact-maps-url" />
            {["fr", "he", "en"].map((lg) => (
              <div key={lg} className="grid md:grid-cols-2 gap-3 border-t border-sand pt-4">
                <Field label={`Adresse ${lg}`} value={content.contact[lg]?.address} dir={lg === "he" ? "rtl" : "ltr"} onChange={(v) => setContent((c) => ({ ...c, contact: { ...c.contact, [lg]: { ...c.contact[lg], address: v } } }))} testid={`contact-address-${lg}`} />
                <Field label={`Horaires ${lg}`} value={content.contact[lg]?.hours} dir={lg === "he" ? "rtl" : "ltr"} onChange={(v) => setContent((c) => ({ ...c, contact: { ...c.contact, [lg]: { ...c.contact[lg], hours: v } } }))} testid={`contact-hours-${lg}`} />
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-sand bg-ivory px-4 py-3 text-center">
      <p className="font-serif text-2xl text-gold">{value}</p>
      <p className="text-xs text-taupe mt-1">{label}</p>
    </div>
  );
}

function Field({ label, value, onChange, dir = "ltr", testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase text-taupe/70 mb-1">{label}</label>
      <input value={value || ""} dir={dir} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-sand bg-ivory px-3 py-2 text-sm text-ink outline-none focus:border-gold" data-testid={testid} />
    </div>
  );
}

function TextField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase text-taupe/70 mb-1">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-sand bg-ivory px-3 py-2 text-sm text-ink outline-none focus:border-gold" data-testid={testid} />
    </div>
  );
}

function TextArea({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase text-taupe/70 mb-1">{label}</label>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3}
        className="w-full rounded-lg border border-sand bg-ivory px-3 py-2 text-sm text-ink outline-none focus:border-gold resize-y" data-testid={testid} />
    </div>
  );
}
